import { useEffect, useRef } from 'react';

import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import LoadingPortal from 'tradera/components/portals/loading-portal';
import { classNames } from 'tradera/utils/class-names';

import styles from './loader.module.scss';

type LoaderComponentProps = React.PropsWithChildren &
    React.HTMLAttributes<HTMLDivElement> & {
        className: string;
        visibleClassName: string;
    };

const LoaderComponent = ({
    className,
    children,
    visibleClassName,
    ...otherProps
}: LoaderComponentProps) => {
    const loaderRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const timeout = setTimeout(() => {
            loaderRef?.current?.classList.add(visibleClassName);
        }, 0);
        return () => clearTimeout(timeout);
    }, [visibleClassName]);

    return (
        <div
            className={className}
            ref={loaderRef}
            role="status"
            aria-live="polite"
            {...otherProps}>
            {children}
        </div>
    );
};

type GlobalLoaderProps = React.PropsWithChildren & {
    blurBackground?: boolean;
};

// Global loading with overlay that covers the whole page, except top menu
export const GlobalLoader = ({
    children,
    blurBackground
}: GlobalLoaderProps) => (
    <LoaderComponent
        className={classNames(
            'loading-portal',
            blurBackground ? 'blurred-background' : 'unblurred-background'
        )}
        visibleClassName="loading-portal-visible">
        {children ? (
            children
        ) : (
            <LoadingIcon
                className="icon icon-spin"
                style={{ width: '5rem', height: '5rem' }}
            />
        )}
    </LoaderComponent>
);

// Legacy global loader that uses portal
export const PortalLoader = () => (
    <LoadingPortal>
        <LoadingIcon
            className="icon icon-spin"
            style={{ width: '5rem', height: '5rem' }}
        />
    </LoadingPortal>
);

type LocalLoaderProps = React.HTMLAttributes<HTMLDivElement> & {
    className?: string;
};

/**
 * Local loader that covers its closest positioned parent
 *
 * Tip: use position-relative class for containing parent
 */
export const LocalLoader = ({ className, ...otherProps }: LocalLoaderProps) => (
    <LoaderComponent
        className={classNames(styles.local, className)}
        visibleClassName={styles.visible}
        {...otherProps}>
        <LoadingIcon className="icon icon-xl g icon-spin" />
    </LoaderComponent>
);

type LocalLoaderWithChildrenProps = React.PropsWithChildren &
    React.HTMLAttributes<HTMLDivElement> & {
        className?: string;
    };

export const LocalLoaderWithChildren = ({
    className,
    children,
    ...otherProps
}: LocalLoaderWithChildrenProps) => (
    <LoaderComponent
        className={classNames(styles.local, className)}
        visibleClassName={styles.visible}
        {...otherProps}>
        {children}
    </LoaderComponent>
);

type LoaderProps = {
    usePortal: boolean;
};

const Loader = ({ usePortal = true }: LoaderProps) => {
    return usePortal ? <PortalLoader /> : <GlobalLoader />;
};

export default Loader;
